<script lang="ts" setup>
import { computed, ref } from 'vue'
import { isEmpty } from 'lodash'
import { usePerson } from '@/queries/people/person'
import { buildImageUrl } from '@/utils/imageUrl'
import DOMPurify from 'dompurify'

interface Props {
    id: number
    name: string
    avatar: string
}

const props = defineProps<Props>()

const hovered = ref(false)
const { data: person } = usePerson(ref(props.id), hovered)

const tooltip = computed(() => {
    const address = person.value?.personDetails?.address
    const email = person.value?.personDetails?.email?.address
    const homePhone = person.value?.personDetails?.phone?.home?.number
    const mobilePhone = person.value?.personDetails?.phone?.mobile?.number
    const workPhone = person.value?.personDetails?.phone?.work?.number

    const cityStateZip = [address?.city, [address?.state, address?.zip].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ')

    const lines = [
        address?.streetAddress ? DOMPurify.sanitize(`<div>${address.streetAddress}</div>`) : '',
        cityStateZip ? DOMPurify.sanitize(`<div>${cityStateZip}</div>`) : '',
        email ? DOMPurify.sanitize(`<div>${email}</div>`) : '',
        homePhone ? DOMPurify.sanitize(`<div>${homePhone} (H)</div>`) : '',
        mobilePhone ? DOMPurify.sanitize(`<div>${mobilePhone} (M)</div>`) : '',
        workPhone ? DOMPurify.sanitize(`<div>${workPhone} (W)</div>`) : ''
    ].filter(Boolean)

    return {
        content: `<div>${lines.join('')}</div>`,
        html: true,
        disabled: isEmpty(person.value) || lines.length === 0
    }
})
</script>

<template>
    <a
        :href="`/people/view/${id}`"
        class="flex items-center gap-4 !p-4 hover:bg-secondary-50 hover:no-underline focus:bg-neutral-100 focus:outline-none"
        @mouseenter="hovered = true">
        <div>
            <img
                v-tooltip.left="tooltip"
                :src="buildImageUrl(avatar)"
                alt=""
                class="w-[55px]" />
        </div>
        <div>
            <span class="text-sm font-medium text-primary">
                {{ name }}
            </span>
        </div>
    </a>
</template>
